import type {Table as ReactTable} from '@tanstack/react-table';
import {ArrowDown, ArrowUp, Plus, X} from 'lucide-react';
import {useTranslation} from 'react-i18next';

import {Button} from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {toggleVariants} from '@/components/ui/toggle';
import {cn} from '@/utils/ui';

interface TableSortProps<TData> {
  table: ReactTable<TData>;
}

function TableSort<TData>({table}: TableSortProps<TData>) {
  const {t} = useTranslation();
  const sortColumns = table
    .getAllColumns()
    .filter(column => column.getCanSort());

  const sortedColumns = sortColumns.filter(column => column.getIsSorted());

  if (!sortColumns.length && !table.getState().sorting.length) {
    return null;
  }

  return (
    <div className="flex flex-row flex-wrap items-center gap-1">
      {!!sortColumns.length && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="small">
              <Plus size={14} />
              {t('Add Sorting')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {sortColumns.map(column => (
              <DropdownMenuItem
                className={cn(
                  'flex items-center gap-1',
                  column.getIsSorted() && 'font-medium text-secondary-500'
                )}
                onClick={() => {
                  column.toggleSorting(undefined, true);
                }}
                key={column.id}
              >
                {column.getIsSorted() === 'asc' ? (
                  <ArrowUp size={12} />
                ) : column.getIsSorted() === 'desc' ? (
                  <ArrowDown size={12} />
                ) : null}
                {column.columnDef?.header?.toString() || column.id}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {sortedColumns
        .sort(
          (columnA, columnB) => columnA.getSortIndex() - columnB.getSortIndex()
        )
        .map(column => (
          <div
            key={column.id}
            className="inline-flex h-11 items-center justify-center"
          >
            <div
              className={cn(
                toggleVariants({variant: 'tag', size: 'small'}),
                'gap-2'
              )}
            >
              <Button
                type="button"
                variant="link-accent"
                size="text"
                className="gap-1.5"
                onClick={() => {
                  column.toggleSorting(column.getIsSorted() !== 'desc', true);
                }}
              >
                {column.getIsSorted() === 'asc' ? (
                  <ArrowUp size={12} />
                ) : (
                  <ArrowDown size={12} />
                )}
                <span>{column.columnDef?.header?.toString() || column.id}</span>
              </Button>
              <Button
                type="button"
                variant="warning"
                size="text"
                className="rounded-full p-0.5"
                onClick={() => {
                  column.clearSorting();
                }}
              >
                <span className="sr-only">
                  {t('Delete')}{' '}
                  {column.columnDef?.header?.toString() || column.id}
                </span>
                <X size={14} />
              </Button>
            </div>
          </div>
        ))}
    </div>
  );
}

export default TableSort;
